import { useState, useRef, useEffect } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Photo Straightener — file-upload only, no sensors. User uploads a
// photo, sees it with a grid overlay and a rotation slider; output is a
// downloadable rotated-and-cropped PNG.
//
// Per pushback: no "auto-straighten" feature. Robust horizon detection
// would need OpenCV-grade vision; doing it badly would frustrate users
// more than helping. Manual slider only, with a clear grid to guide.
//
// Crop policy: when rotated, the output is auto-cropped to the largest
// axis-aligned rectangle fully inside the rotated image. This matches
// every other "straighten" tool the user has seen.

const GRID_LINES = 9; // 3 vertical + 3 horizontal "rule of thirds"-like

export default function PhotoStraightener() {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [angle, setAngle] = useState(0); // -45..45
  const [showGrid, setShowGrid] = useState(true);
  const canvasRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const url = URL.createObjectURL(f);
    setImgUrl(url);
    const im = new Image();
    im.onload = () => setImgEl(im);
    im.src = url;
  };

  // Compute the rotated+cropped image on every angle change.
  useEffect(() => {
    if (!imgEl || !canvasRef.current) return;
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    const w = imgEl.naturalWidth, h = imgEl.naturalHeight;
    if (!w || !h) return;
    const rad = (angle * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
    // After rotating by `rad`, the largest axis-aligned crop that fits
    // strictly inside the rotated image has dimensions:
    //   ar = w / h
    //   if ar > 1: crop w = w*cos - h*sin / (something) — but a cleaner
    //   closed-form for the inscribed-rect-of-a-rotated-rect problem:
    const ar = w / h;
    let cropW, cropH;
    if (ar >= 1) {
      cropW = (w * Math.cos(Math.abs(rad)) - h * Math.sin(Math.abs(rad)));
      cropH = cropW / ar;
    } else {
      cropH = (h * Math.cos(Math.abs(rad)) - w * Math.sin(Math.abs(rad)));
      cropW = cropH * ar;
    }
    // Above formula can go negative for large angles; clamp:
    cropW = Math.max(10, cropW);
    cropH = Math.max(10, cropH);

    // Render at the cropped size at full resolution so the output
    // download is high quality.
    cv.width = Math.round(cropW);
    cv.height = Math.round(cropH);
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.save();
    ctx.translate(cv.width / 2, cv.height / 2);
    ctx.rotate(-rad);
    ctx.drawImage(imgEl, -w / 2, -h / 2);
    ctx.restore();
  }, [imgEl, angle]);

  const download = () => {
    if (!canvasRef.current) return;
    const a = document.createElement("a");
    a.href = canvasRef.current.toDataURL("image/png");
    a.download = "straightened.png";
    a.click();
  };

  return (
    <ToolsShell title="Photo Straightener">
      {!imgEl && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Upload a photo and use the slider to straighten it. Output is auto-cropped to the largest rectangle that fits inside the rotated image.
          </p>
          <label style={{ ...primaryBtn, display: "inline-block" }}>
            <input type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
            Choose photo
          </label>
          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14 }}>
            Your file never leaves your device. Processing happens locally in your browser.
          </p>
        </div>
      )}

      {imgEl && (
        <>
          <div style={{ position: "relative", maxWidth: "100%", margin: "0 auto", textAlign: "center" }}>
            <canvas
              ref={canvasRef}
              style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: 8, background: "#000", display: "inline-block" }}
            />
            {showGrid && <GridOverlay />}
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={{ fontSize: 13, color: "#9A9DBE", display: "flex", justifyContent: "space-between" }}>
              <span>Rotation</span>
              <span style={{ color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{angle.toFixed(1)}°</span>
            </label>
            <input
              type="range"
              min="-15"
              max="15"
              step="0.1"
              value={angle}
              onChange={(e) => setAngle(parseFloat(e.target.value))}
              style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 8 }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#5B5E7A" }}>
              <span>-15°</span><span>0°</span><span>+15°</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            <button onClick={() => setAngle(0)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              Reset to 0°
            </button>
            <button onClick={() => setShowGrid((g) => !g)} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              {showGrid ? "Hide" : "Show"} grid
            </button>
            <label style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
              <input type="file" accept="image/*" onChange={onFile} style={{ display: "none" }} />
              Choose different photo
            </label>
            <button onClick={download} style={{ ...primaryBtn, marginLeft: "auto" }}>
              Download PNG
            </button>
          </div>
        </>
      )}
    </ToolsShell>
  );
}

function GridOverlay() {
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      {[1, 2].map((i) => (
        <div key={`h${i}`} style={{ position: "absolute", left: "10%", right: "10%", top: `${(i * 100) / 3}%`, height: 1, background: "rgba(255,255,255,0.3)" }} />
      ))}
      {[1, 2].map((i) => (
        <div key={`v${i}`} style={{ position: "absolute", top: "10%", bottom: "10%", left: `${(i * 100) / 3}%`, width: 1, background: "rgba(255,255,255,0.3)" }} />
      ))}
    </div>
  );
}
